import Config from '../messages';
import Emitter from './eventEmitter';

/**
 * Hash a string with SHA-256
 *
 * @param buffer
 * @returns {Promise<Uint8Array>}
 */
export const sha256 = async (buffer) => {
    const digest = await crypto.subtle.digest('SHA-256', buffer);
    return new Uint8Array(digest);
};

/**
 * Create a Base64-encoded string
 *
 * @param arrayBuffer
 * @returns {string}
 */
export const base64URLEncode = (arrayBuffer) => {
    return btoa(String.fromCharCode.apply(null, arrayBuffer))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
};

/**
 * Generate a code verifier for Keycloak
 *
 * @returns {string}
 */
export const generateCodeVerifier = () => {
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    return base64URLEncode(array);
};

/**
 * Create and store a code verifier for Keycloak challenge
 *
 * @returns {string}
 */
export const createCodeVerifier = () => {
    const codeVerifier = generateCodeVerifier();
    localStorage.setItem('code_verifier', codeVerifier);
    sessionStorage.setItem('code_verifier', codeVerifier);
    return codeVerifier;
};

/**
 * Generate a code challenge for Keycloak
 *
 * @param codeVerifier
 * @returns {Promise<string>}
 */
export const generateCodeChallenge = async (codeVerifier) => {
    const verifierBytes = new TextEncoder().encode(codeVerifier);
    const hashed = await sha256(verifierBytes);
    return base64URLEncode(hashed);
};

/**
 * Decode a JWT token to extract its components
 *
 * @param token
 * @returns {{sig: *, payload: any, header: any}}
 */
export const decodeJWTToken = (token) => {
    const parts = token.split('.');
    const header = JSON.parse(atob(parts[0]));
    const payload = JSON.parse(atob(parts[1]));
    const signature = parts[2];
    return {
        header: header,
        payload: payload,
        sig: signature
    };
};

/**
 * Return a set of Now, Expired, and Warning dates for token processing
 *
 * @param exp
 * @returns {{nowDate: Date, warningDate: Date, expDate: Date}}
 */
export const makeDatesForToken = (exp) => {
    let nowDate = new Date();
    let expDate = new Date((exp * 1000));
    let warningDate = new Date((exp * 1000) - 600000);
    return {
        nowDate: nowDate,
        expDate: expDate,
        warningDate: warningDate
    }
};

/**
 * Compare expiration dates to calculate token expiration
 *
 * @param exp
 * @returns {{expired: boolean, withinWarning: boolean}}
 */
export const checkTokenDates = (exp) => {
    let response = {
        expired: false,
        withinWarning: false
    };
    const dates = makeDatesForToken(exp);

    if ((dates.expDate - dates.nowDate) <= 0) {
        // Expiration date has passed
        response.expired = true;
    } else if ((dates.expDate - dates.warningDate) <= 0) {
        // Within 10-minute warning time
        response.withinWarning = true;
    }
    return response;
};

/**
 * Check token for expiration status and store result
 *
 * @param token
 * @returns {{expired: boolean, withinWarning: boolean}}
 */
export const checkTokenStatus = (token) => {
    const tokenToCheck = (token) ? token : localStorage.getItem('token');
    if (!tokenToCheck) {
        Emitter.emit('token_state');
        sessionStorage.setItem('token_state', 'expired');
        return {
            expired: true,
            withinWarning: false
        }
    }
    const decodedToken = decodeJWTToken(tokenToCheck);
    const exp = (decodedToken.payload && decodedToken.payload.exp) ? decodedToken.payload.exp : 0;
    let tokenStatus = checkTokenDates(exp);
    let tokenState = 'valid';
    if (tokenStatus.expired || tokenStatus.withinWarning) {
        tokenState = (tokenStatus.withinWarning) ? 'warning' : 'expired';
        Emitter.emit('token_state');
    }
    sessionStorage.setItem('token_state', tokenState);
    return tokenStatus;
};

/**
 * Assemble Keycloak account endpoint
 *
 * @returns {string}
 */
export const getKeycloakAccountUrl = () => {
    const kcBase = Config.fuse.api.KEYCLOAK_AUTH_URI;
    const realm = Config.fuse.api.REALM_PATH;
    const endpoint = Config.fuse.api.KEYCLOAK_ACCOUNT_ENDPOINT;
    // Add redirect with query if Account can be set up to return to Admin
    //const redirect = window.location.href;
    //const query = `?client_id=account&redirect_uri=${encodeURIComponent(redirect)}`;
    return kcBase + realm + endpoint;
};

/**
 * Assemble Keycloak auth endpoint
 *
 * @param challenge
 * @param isLogin
 * @returns {string}
 */
export const getKeycloakAuthUrl = (challenge, isLogin = true) => {
    const kcBase = Config.fuse.api.KEYCLOAK_AUTH_URI;
    const realm = Config.fuse.api.REALM_PATH;
    const endpoint = Config.fuse.api.KEYCLOAK_AUTH_ENDPOINT;
    const redirect = (isLogin) ? window.location.origin + '/#/login' : window.location.href;
    const query = `?client_id=user&response_type=code&scope=openid&redirect_uri=${encodeURIComponent(redirect)}&code_challenge_method=S256&code_challenge=${challenge}`;
    return kcBase + realm + endpoint + query;
};

/**
 * Assemble Keycloak token endpoint
 *
 * @returns {string}
 */
export const getKeycloakTokenUrl = () => {
    const kcBase = Config.fuse.api.KEYCLOAK_AUTH_URI;
    const realm = Config.fuse.api.REALM_PATH;
    const endpoint = Config.fuse.api.KEYCLOAK_TOKEN_ENDPOINT;
    return kcBase + realm + endpoint;

};

/**
 * Assemble Keycloak logout endpoint
 *
 * @returns {string}
 */
export const getKeycloakLogoutUrl = () => {
    const kcBase = Config.fuse.api.KEYCLOAK_AUTH_URI;
    const realm = Config.fuse.api.REALM_PATH;
    const endpoint = Config.fuse.api.KEYCLOAK_LOGOUT_ENDPOINT;
    const redirect = window.location.origin + '/';
    let query = `?client_id=user&post_logout_redirect_uri=${encodeURIComponent(redirect)}`;
    const idToken = localStorage.getItem('kc_id_token');
    if (idToken) {
        query += '&id_token_hint=' + idToken;
    }
    return kcBase + realm + endpoint + query;
};



// WEBPACK FOOTER //
// ./src/Fuse/shared/tokenFunctions.js